import { trackEvent as trackEventService } from '@/services/tracking.service';
import { useUserStore } from '@/stores/user/userStore';

/**
 * Helper function to determine if the current context is in the main app
 * Based on the same logic used in axios interceptor
 * @returns {boolean} True if in main app, false if in embed/share context
 */
const isInAppContext = () => {
  return !(window.location.pathname.startsWith('/embed') || window.location.pathname.startsWith('/share'));
};

/**
 * Helper function to track events for the admin dashboard
 * Automatically determines whether to include user_id based on app context
 *
 * @param {string} event_name - The name of the event to track
 * @param {Object} event_props - Event properties (should include kb_id and other relevant data)
 * @param {boolean|null} isInApp - Optional override for app context detection. If null, auto-detects based on URL
 * @returns {Promise} The HTTP response from the tracking service
 *
 * @example
 */
export const trackEvent = async (event_name, event_props = {}, isInApp = null) => {
  try {
    // Validate required parameters
    if (!event_name || typeof event_name !== 'string') {
      throw new Error('event_name is required and must be a string');
    }

    if (!event_props.kb_id) {
      throw new Error('kb_id is required in event_props');
    }

    // Determine app context - use provided value or auto-detect
    const inAppContext = isInApp !== null ? isInApp : isInAppContext();

    // Get user information from store
    const userStore = useUserStore.getState();
    const user_id = userStore.user?.id;

    // Prepare event data based on context
    const event_data = { ...event_props };

    // Add user_id if in app context and user is available
    if (inAppContext && user_id) {
      event_data.user_id = user_id;
    }

    // Call the tracking service
    return await trackEventService(event_props.kb_id, event_name, event_data);
  } catch (error) {
    console.error('Error tracking event:', error);
    throw error;
  }
};
